import { Link } from "wouter";
import { ArrowRight, BookOpen } from "lucide-react";
import AnimatedSection from "@/components/AnimatedSection";

interface ArticleLink {
  title: string;
  slug: string;
  href?: string;
  excerpt: string;
  typeLabel?: string;
  ctaLabel?: string;
}

interface RelatedArticlesProps {
  heading?: string;
  subheading?: string;
  articles: ArticleLink[];
}

export default function RelatedArticles({
  heading = "Fördjupa dig i vår kunskapsbank",
  subheading = "Läs våra artiklar för att lära dig mer om material, teknik och bästa praxis inom metallbearbetning.",
  articles,
}: RelatedArticlesProps) {
  return (
    <section className="bg-gray-50 py-16 md:py-20 border-t border-gray-200">
      <div className="container">
        <AnimatedSection>
          <div className="max-w-2xl mb-10">
            <span className="text-[#2563EB] text-sm font-medium tracking-wider uppercase">
              Kunskapsbank
            </span>
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-gray-900 mt-3">
              {heading}
            </h2>
            <p className="text-gray-500 mt-3 leading-relaxed">{subheading}</p>
          </div>
        </AnimatedSection>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article, i) => (
            <AnimatedSection key={article.href ?? article.slug} delay={i * 100}>
              <Link
                href={article.href ?? `/nyheter/${article.slug}`}
                className="group block h-full"
              >
                <div className="bg-white border border-gray-200 rounded-lg p-6 h-full hover:border-[#2563EB]/30 hover:shadow-md transition-all duration-300 flex flex-col">
                  <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-4 h-4 text-[#2563EB]" />
                    <span className="text-[#2563EB] text-xs font-medium uppercase tracking-wider">
                      {article.typeLabel ?? "Artikel"}
                    </span>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-gray-900 group-hover:text-[#2563EB] transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-gray-500 text-sm mt-2 leading-relaxed flex-1">
                    {article.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-1 text-[#2563EB] text-sm font-medium mt-4 group-hover:gap-2 transition-all">
                    {article.ctaLabel ?? "Läs artikeln"} <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            </AnimatedSection>
          ))}
        </div>
      </div>
    </section>
  );
}
